import { useMemo, useState, useRef, Fragment } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Download, FileSpreadsheet, X, Check, GripVertical, Lock, Unlock, RefreshCw } from "lucide-react";
import MoreMenu from "@/components/MoreMenu";
import ImportHub from "@/components/ImportHub";
import { api, API_BASE, formatApiError, tokenStore, fmtInt } from "@/lib/api";
import { MoneyInput } from "@/components/MoneyInput";
import AiPdfExtractDialog from "@/components/AiPdfExtractDialog";
import DemoSampleDialog from "@/components/DemoSampleDialog";
import { saveOrDownload, useDownloadFolderName } from "@/lib/downloadFolder";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { BtwHint } from "@/components/btw/BtwHint";
import { BtwBlock } from "@/components/btw/BtwBlock";
import { btwContext, btwForRow } from "@/lib/btw";

export async function downloadXlsxFrom(endpoint, mode, t, scope) {
  if (!endpoint) return;
  const url = `${API_BASE}${endpoint}`;
  const token = tokenStore.get();
  try {
    const res = await fetch(url, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) {
      toast.error(mode === "template" ? t("Sjabloon downloaden mislukt") : t("Export mislukt"));
      return;
    }
    const blob = await res.blob();
    const cd = res.headers.get("content-disposition") || "";
    const m = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
    const filename = (m && decodeURIComponent(m[1])) || endpoint.split("?")[0].split("/").pop();
    const wrote = await saveOrDownload(blob, filename, scope);
    if (wrote) toast.success(t("Opgeslagen in map ‘{name}’").replace("{name}", filename));
  } catch {
    toast.error(mode === "template" ? t("Sjabloon downloaden mislukt") : t("Export mislukt"));
  }
}

// --- Mapping ---
// The visible aggregate rows in the current model are strictly one field each:
// INKOMSTEN = revenue, KOSTEN = operating_costs. AFSCHRIJVING is its own main
// category above EXPL. RESULTAAT. `other_income` is legacy data that is no
// longer surfaced (its values are ignored by the residual calculation so that
// Overige inkomsten = revenue − Σ user revenue subposten).
const CAT_TO_BD_FIELD = {
  revenue: "revenue_breakdown",
  opex: "opex_breakdown",
};
const AGG_FIELDS_BY_GROUP = {
  // Overige baten (subsidies, giften, bijdragen) tellen mee bij Inkomsten
  // zodat Expl. resultaat = totale baten − totale lasten − afschrijvingen.
  // Zonder deze aggregatie loopt "Netto expl. resultaat" niet in de pas met
  // de winst die aan de Algemene reserve wordt toegevoegd.
  inkomsten: ["revenue", "other_income"],
  uitgaven: ["operating_costs"],
};

export function rowToPayload(row) {
  return {
    year: row.year,
    revenue: row.revenue || 0,
    operating_costs: row.operating_costs || 0,
    depreciation: row.depreciation || 0,
    interest_paid: row.interest_paid || 0,
    interest_income: row.interest_income || 0,
    other_income: row.other_income || 0,
    balance: row.balance || 0,
    cash_bank_balance: row.cash_bank_balance || 0,
    revenue_breakdown: row.revenue_breakdown || [],
    opex_breakdown: row.opex_breakdown || [],
    other_income_breakdown: row.other_income_breakdown || [],
    // Bug-fix: without preserving reserves_breakdown here, any cell-edit
    // (rentelasten / rentebaten / revenue subposts / etc.) wiped the reserves
    // dotaties/onttrekkingen for that year, because the schema defaults it to [].
    reserves_breakdown: row.reserves_breakdown || [],
    notes: row.notes || "",
    // Kasstroom-opbouw (liquiditeitsimport / BTW-ijkpunt): meesturen, anders zet het schema ze op 0.
    ...Object.fromEntries(LIQUIDITY_FIELDS.map((k) => [k, Number(row[k]) || 0])),
  };
}

const LIQUIDITY_FIELDS = [
  "liquide_01_jan", "afschr_invest_next_years", "dotaties_voorzieningen", "onttrekkingen_voorzieningen",
  "bestemmingsreserves_resultaat", "mutatie_werkkapitaal", "investeringen_materiele_activa",
  "verhoging_kortlopende_schulden", "verhoging_langlopende_schulden", "mutatie_debiteuren_crediteuren", "btw_afdracht_teruggave",
];

function findSubpostAmount(row, cat, name) {
  const arr = row[CAT_TO_BD_FIELD[cat]] || [];
  const hit = arr.find((x) => x.name === name);
  return hit ? Number(hit.amount) || 0 : 0;
}

export default function HistoricalsMatrix({ t, clubId, club, historicals, subposts, onChange, locked = false, clubLocked = false, canAiImport = true, userTier, onToggleLock }) {
  const fileRef = useRef();
  const detailedFileRef = useRef();
  const folderName = useDownloadFolderName(clubId);
  const uploadTip = folderName
    ? t("Zoek je bestand in de gekozen map ‘{name}’").replace("{name}", folderName)
    : t("Nog geen downloadmap voor deze club — kies er één bovenaan de pagina om exports en imports op één plek te bundelen.");

  const rows = useMemo(() => [...historicals].sort((a, b) => (a.year || 0) - (b.year || 0)), [historicals]);
  const btwCtx = useMemo(() => btwContext(club, subposts), [club, subposts]);

  // Subposts grouped per category (already position-sorted by API).
  const subsByCat = useMemo(() => {
    const g = { revenue: [], opex: [] };
    for (const s of subposts || []) if (g[s.category]) g[s.category].push(s);
    return g;
  }, [subposts]);

  // ---- Field / breakdown mutations ----
  const putRow = async (row, patch) => {
    try {
      await api.put(`/historicals/${row.id}`, { ...rowToPayload(row), ...patch });
      markDirty();
      onChange?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveField = (row, key, val) => {
    const num = Number(val);
    const amount = Number.isFinite(num) ? num : 0;
    if (Number(row[key] || 0) === amount) return;
    putRow(row, { [key]: amount });
  };
  const saveSubpost = (row, cat, name, val) => {
    const num = Number(val);
    const amount = Number.isFinite(num) ? num : 0;
    const bdField = CAT_TO_BD_FIELD[cat];
    const list = (row[bdField] || []).slice();
    const idx = list.findIndex((x) => x.name === name);
    if (idx >= 0) list[idx] = { name, amount };
    else if (amount !== 0) list.push({ name, amount });
    else return;
    putRow(row, { [bdField]: list });
  };

  // ---- Year management ----
  const [yearDlg, setYearDlg] = useState(false);
  const [aiPdfOpen, setAiPdfOpen] = useState(false);
  const [aiPdfInitial, setAiPdfInitial] = useState(null);
  const [demoSampleOpen, setDemoSampleOpen] = useState(false);
  const [resetDlg, setResetDlg] = useState(false);
  const [resetValues, setResetValues] = useState({});
  const [newYear, setNewYear] = useState(() => {
    const now = new Date().getFullYear();
    const yrs = rows.map((r) => r.year);
    return yrs.length ? Math.max(...yrs) + 1 : now - 1;
  });
  const addYear = async () => {
    const y = parseInt(newYear, 10);
    if (!y || y < 1900 || y > 2100) { toast.error(t("Ongeldig jaartal")); return; }
    if (rows.some((r) => r.year === y)) { toast.error(t("Dit jaar bestaat al")); setYearDlg(false); return; }
    try {
      await api.post(`/clubs/${clubId}/historicals`, { year: y, revenue: 0, operating_costs: 0, depreciation: 0, interest_paid: 0, interest_income: 0, other_income: 0, balance: 0, cash_bank_balance: 0 });
      toast.success(t("Jaar toegevoegd"));
      setYearDlg(false);
      onChange?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteYear = async (row) => {
    if (!window.confirm(t("Jaar {year} verwijderen? Alle bedragen (incl. subposten) worden gewist.").replace("{year}", row.year))) return;
    try {
      await api.delete(`/historicals/${row.id}`);
      toast.success(t("Jaar verwijderd"));
      onChange?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // ---- Reset costs aggregate (P1 helper) ----
  const openResetDlg = () => {
    const initial = {};
    rows.forEach((r) => { initial[r.year] = String(Math.round(Number(r.operating_costs) || 0)); });
    setResetValues(initial);
    setResetDlg(true);
  };
  const submitReset = async () => {
    // Convert values → numbers; skip empty entries so the user can leave years untouched.
    const payload = {};
    Object.entries(resetValues).forEach(([k, v]) => {
      const s = String(v || "").trim();
      if (!s) return;
      const n = parseInt(s, 10);
      if (Number.isFinite(n)) payload[k] = n;
    });
    if (!Object.keys(payload).length) { toast.error(t("Vul minstens één bedrag in.")); return; }
    try {
      const { data } = await api.post(`/clubs/${clubId}/reset-costs-aggregate`, { values: payload });
      toast.success(t("{n} boekjaren bijgewerkt").replace("{n}", data.updated));
      setResetDlg(false);
      onChange?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // ---- Subpost create / delete ----
  const [addingCat, setAddingCat] = useState(null);
  const [newSpName, setNewSpName] = useState("");
  // Toont een "Herbereken" knop na iedere edit tot de gebruiker klikt of tot de volgende
  // load-cyclus (na PUT) het venster weer sluit. Zo geven we een tastbare feedback voor
  // afgeleide rijen (Overige kosten / Expl. resultaat / Netto expl. resultaat).
  const [dirty, setDirty] = useState(false);
  const markDirty = () => setDirty(true);
  const refresh = async () => {
    setDirty(false);
    await onChange?.();
    toast.success(t("Herberekend"));
  };
  const startAddSub = (cat) => { setAddingCat(cat); setNewSpName(""); };
  const cancelAddSub = () => { setAddingCat(null); setNewSpName(""); };
  const submitAddSub = async (cat) => {
    const name = (newSpName || "").trim();
    if (!name) { cancelAddSub(); return; }
    try {
      await api.post(`/clubs/${clubId}/subposts`, { category: cat, name });
      toast.success(t("Subpost toegevoegd"));
      cancelAddSub();
      onChange?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteSubpost = async (sp) => {
    if (!window.confirm(t("Subpost verwijderen? Bijbehorende bedragen in historische jaren worden ook gewist."))) return;
    try {
      await api.delete(`/subposts/${sp.id}`);
      toast.success(t("Subpost verwijderd"));
      onChange?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // ---- Subpost drag & drop reorder (intra-category) ----
  const dragState = useRef({ cat: null, id: null });
  const onDragStart = (cat, id) => (e) => {
    dragState.current = { cat, id };
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", id);
  };
  const onDragOver = (cat) => (e) => {
    if (dragState.current.cat === cat) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }
  };
  const onDrop = (cat, targetId) => async (e) => {
    e.preventDefault();
    const srcId = dragState.current.id;
    if (!srcId || dragState.current.cat !== cat || srcId === targetId) return;
    const list = subsByCat[cat].slice();
    const srcIdx = list.findIndex((x) => x.id === srcId);
    const tgtIdx = list.findIndex((x) => x.id === targetId);
    if (srcIdx === -1 || tgtIdx === -1) return;
    const [moved] = list.splice(srcIdx, 1);
    list.splice(tgtIdx, 0, moved);
    // Push new positions (1-indexed) to backend.
    try {
      await Promise.all(list.map((sp, idx) => api.put(`/subposts/${sp.id}`, { category: sp.category, name: sp.name, position: idx + 1 })));
      onChange?.();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
  };

  // ---- Upload handler ----
  const uploadFile = async (endpoint, file) => {
    const form = new FormData();
    form.append("file", file);
    try {
      await api.post(`/clubs/${clubId}/${endpoint}`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("Import geslaagd"));
      onChange?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Computed per-year totals for aggregate rows.
  const yearSum = (r, keys) => keys.reduce((a, k) => a + (Number(r[k]) || 0), 0);
  const inkomstenFor = (r) => yearSum(r, AGG_FIELDS_BY_GROUP.inkomsten);
  const uitgavenFor = (r) => yearSum(r, AGG_FIELDS_BY_GROUP.uitgaven);
  const afschrijvingFor = (r) => Number(r.depreciation) || 0;
  // EXPL. RESULTAAT = INKOMSTEN − KOSTEN − AFSCHRIJVING
  const resultaatFor = (r) => inkomstenFor(r) - uitgavenFor(r) - afschrijvingFor(r);
  const sumBd = (r, cat) => (r[CAT_TO_BD_FIELD[cat]] || []).reduce((a, b) => a + (Number(b.amount) || 0), 0);
  // Overige inkomsten = INKOMSTEN − Σ user revenue subposten
  const overigeInkomstenFor = (r) => inkomstenFor(r) - sumBd(r, "revenue");
  // Overige kosten = KOSTEN − Σ user opex subposten (residu binnen de kosten-groep)
  const overigeKostenFor = (r) => uitgavenFor(r) - sumBd(r, "opex");

  return (
    <div className="bg-card border-2 border-foreground heavy-shadow" data-testid="hist-matrix">
      {/* ---- Toolbar ---- */}
      <div className="p-5 border-b-2 border-foreground flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="font-display font-black text-2xl uppercase tracking-tight">{t("Voorgaande jaren")}</h2>
          <BtwHint club={club} clubId={clubId} testid="hist-btw-hint" />
          {onToggleLock && (
            <button
              onClick={onToggleLock}
              data-testid="hist-lock-toggle"
              title={locked ? t("Vergrendeld — klik om te ontgrendelen") : t("Klik om te vergrendelen zodat historische data niet meer gewijzigd kan worden")}
              className={`btn-brutal px-3 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-1.5 ${locked ? "bg-destructive text-background" : "bg-background text-foreground"}`}
            >
              {locked ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
              {locked ? t("Vergrendeld") : t("Ontgrendeld")}
            </button>
          )}
          {/* Row-management actions grouped next to the lock toggle to keep the
              main toolbar (import/export/sjabloon) uncluttered. */}
          <button onClick={() => setYearDlg(true)} data-testid="hist-add-year-btn"
            disabled={locked}
            className={`btn-brutal bg-primary text-primary-foreground px-3 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-1.5 ${locked ? "opacity-40 cursor-not-allowed" : ""}`}>
            <Plus className="w-3.5 h-3.5" /> {t("Jaar toevoegen")}
          </button>
          {dirty && (
            <button
              onClick={refresh}
              data-testid="hist-recompute-btn"
              title={t("Waardes zijn gewijzigd — klik om afgeleide rijen (Overige kosten, Exploitatieresultaat, Netto exploitatieresultaat) opnieuw te berekenen.")}
              className="btn-brutal bg-primary text-primary-foreground px-3 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-1.5 animate-pulse">
              <RefreshCw className="w-3.5 h-3.5" />
              {t("Herbereken")}
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input ref={fileRef} type="file" accept=".xlsx,.xlsm,.csv" className="hidden" data-testid="hist-file-input"
            onChange={(e) => { if (e.target.files?.[0]) { uploadFile("import/historicals", e.target.files[0]); e.target.value = ""; } }} />
          <input ref={detailedFileRef} type="file" accept=".xlsx,.xlsm,.csv" className="hidden" data-testid="hist-detailed-file-input"
            onChange={(e) => { if (e.target.files?.[0]) { uploadFile("import/historicals-detailed", e.target.files[0]); e.target.value = ""; } }} />
          <MoreMenu size="sm" testid="hist-more-menu" items={[
            { label: t("Excel exporteren (met subposten)"), icon: <Download className="w-4 h-4" />, onClick: () => downloadXlsxFrom(`/clubs/${clubId}/export/historicals-detailed.xlsx`, "export", t, clubId), testid: "hist-detailed-export-btn" },
            { label: t("Excel exporteren (alleen totalen)"), icon: <Download className="w-4 h-4" />, onClick: () => downloadXlsxFrom(`/clubs/${clubId}/export/historicals.xlsx`, "export", t, clubId), testid: "hist-export-btn" },
            "separator",
            { label: t("Sjabloon met subposten"), icon: <FileSpreadsheet className="w-4 h-4" />, onClick: () => downloadXlsxFrom(`/clubs/${clubId}/templates/historicals-detailed.xlsx`, "template", t, clubId), testid: "hist-detailed-template-btn" },
            { label: t("Sjabloon alleen totalen"), icon: <FileSpreadsheet className="w-4 h-4" />, onClick: () => downloadXlsxFrom("/templates/historicals.xlsx", "template", t, clubId), testid: "hist-template-btn" },
            ...(rows.length > 0 ? ["separator", { label: t("Reset KOSTEN-totalen"), icon: <RefreshCw className="w-4 h-4" />, onClick: openResetDlg, disabled: locked, testid: "hist-reset-costs-btn", hint: t("Herstel totalen") }] : []),
          ]} />
        </div>
      </div>

      <ImportHub
        variant={rows.length === 0 ? "start" : "compact"}
        testid="hist-import-hub"
        locked={locked}
        lockedHint={clubLocked ? t("Club is vergrendeld — ontgrendel de club via 'Meer' bovenaan de pagina.") : t("Historische data is vergrendeld — klik op het slot naast de tabeltitel om te ontgrendelen.")}
        ai={{
          label: userTier === "TRIAL" && !canAiImport ? t("AI-demo: jaarverslag (PDF)") : t("AI-import jaarverslag (PDF)"),
          description: userTier === "TRIAL" && !canAiImport
            ? t("Kies een demo-jaarverslag en zie hoe de AI de cijfers per boekjaar en subpost inleest.")
            : t("Upload de jaarrekening als PDF — de AI leest inkomsten, kosten, afschrijvingen en kas per boekjaar en vult de subposten voor je in. Je controleert alles vóór het opslaan."),
          badge: !canAiImport ? (userTier === "TRIAL" ? t("Demo") : "PRO") : null,
          muted: !canAiImport && userTier !== "TRIAL",
          title: locked
            ? (clubLocked ? t("Club is vergrendeld — ontgrendel de club om de AI-import te gebruiken.") : t("Historische data is vergrendeld — klik op het slot naast de tabeltitel om te ontgrendelen."))
            : (!canAiImport ? (userTier === "TRIAL" ? t("Klik om een demo-jaarverslag te kiezen (proefperiode).") : t("AI-import zit in het STANDARD- of PREMIUM-abonnement.")) : t("AI leest je jaarverslag (PDF) en vult automatisch de historische data in.")),
          onClick: () => {
            if (locked) {
              toast.error(clubLocked
                ? t("Club is vergrendeld — ontgrendel de club bovenaan de pagina om de AI-import te gebruiken.")
                : t("Historische data is vergrendeld — klik op het slot naast de tabeltitel om te ontgrendelen."));
              return;
            }
            if (!canAiImport && userTier === "TRIAL") { setDemoSampleOpen(true); return; }
            if (!canAiImport) { toast.error(t("AI-import zit in het STANDARD- of PREMIUM-abonnement. Upgrade om je jaarverslag automatisch in te laden.")); return; }
            setAiPdfInitial(null);
            setAiPdfOpen(true);
          },
        }}
        excel={{
          templateLabel: t("Sjabloon downloaden (met subposten)"),
          onTemplate: () => downloadXlsxFrom(`/clubs/${clubId}/templates/historicals-detailed.xlsx`, "template", t, clubId),
          importLabel: t("Ingevuld sjabloon importeren"),
          onImport: () => detailedFileRef.current?.click(),
          importTitle: uploadTip,
          folderName,
          alt: { label: t("Alleen totalen, zonder subposten"), onTemplate: () => downloadXlsxFrom("/templates/historicals.xlsx", "template", t, clubId), onImport: () => fileRef.current?.click() },
        }}
        manual={{ label: t("Handmatig: jaar toevoegen"), onClick: () => setYearDlg(true) }}
      />

      {/* ---- Matrix ---- */}
      {rows.length > 0 && (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-foreground text-background">
              <th className="text-left px-4 py-2 font-display uppercase text-xs tracking-widest sticky left-0 bg-foreground z-10 min-w-[240px]">{t("Post")}</th>
              {rows.map((r) => (
                <th key={r.id} className="px-3 py-2 font-display uppercase text-xs tracking-widest text-right">
                  <div className="flex items-center justify-end gap-1">
                    <span data-testid={`hist-year-header-${r.year}`}>{r.year}</span>
                    {!locked && (
                      <button onClick={() => deleteYear(r)} data-testid={`hist-year-del-${r.year}`}
                        className="opacity-40 hover:opacity-100 hover:text-destructive" title={t("Jaar verwijderen")}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </th>
              ))}
              {rows.length === 0 && (
                <th className="px-3 py-2 font-display uppercase text-xs tracking-widest text-center opacity-60">{t("Klik ‘Jaar toevoegen’ om te starten")}</th>
              )}
            </tr>
          </thead>
          <tbody>
            {/* ============================================================
                INKOMSTEN
                ============================================================ */}
            <AggregateRow label={t("Inkomsten")} rows={rows} sumFn={inkomstenFor} testid="agg-inkomsten"
                          editable onCommit={(r, v) => saveField(r, "revenue", v)}
                          getInitial={(r) => r.revenue || 0} locked={locked} />
            {subsByCat.revenue.map((sp) => (
              <SubpostRow key={sp.id} sp={sp} rows={rows} cat="revenue" testid={`hist-sp-${sp.id}`}
                          value={(r) => findSubpostAmount(r, "revenue", sp.name)}
                          onCommit={(r, v) => saveSubpost(r, "revenue", sp.name, v)}
                          onDragStart={onDragStart("revenue", sp.id)} onDragOver={onDragOver("revenue")} onDrop={onDrop("revenue", sp.id)}
                          onDelete={() => deleteSubpost(sp)} t={t} locked={locked} />
            ))}
            {/* Computed system subpost: Overige inkomsten = INKOMSTEN − Σ subposten */}
            <ComputedSystemRow label={t("Overige inkomsten")} rows={rows} testid="sys-overige-inkomsten"
                               value={overigeInkomstenFor} t={t} />
            <AddSubpostRow cat="revenue" colSpan={rows.length + 1}
                           adding={addingCat === "revenue"} newName={newSpName}
                           onStart={() => startAddSub("revenue")} onCancel={cancelAddSub}
                           onChange={setNewSpName} onSubmit={() => submitAddSub("revenue")} t={t} locked={locked} />

            {/* ============================================================
                KOSTEN
                ============================================================ */}
            <AggregateRow label={t("Kosten")} rows={rows} sumFn={uitgavenFor} testid="agg-uitgaven"
                          editable onCommit={(r, v) => saveField(r, "operating_costs", v)}
                          getInitial={(r) => r.operating_costs || 0} locked={locked} />
            {subsByCat.opex.map((sp) => (
              <SubpostRow key={sp.id} sp={sp} rows={rows} cat="opex" testid={`hist-sp-${sp.id}`}
                          value={(r) => findSubpostAmount(r, "opex", sp.name)}
                          onCommit={(r, v) => saveSubpost(r, "opex", sp.name, v)}
                          onDragStart={onDragStart("opex", sp.id)} onDragOver={onDragOver("opex")} onDrop={onDrop("opex", sp.id)}
                          onDelete={() => deleteSubpost(sp)} t={t} locked={locked} />
            ))}
            {/* Computed system subpost: Overige kosten */}
            <ComputedSystemRow label={t("Overige kosten")} rows={rows} testid="sys-overige-kosten"
                               value={overigeKostenFor} t={t} />
            <AddSubpostRow cat="opex" colSpan={rows.length + 1}
                           adding={addingCat === "opex"} newName={newSpName}
                           onStart={() => startAddSub("opex")} onCancel={cancelAddSub}
                           onChange={setNewSpName} onSubmit={() => submitAddSub("opex")} t={t} locked={locked} />

            {/* ============================================================
                AFSCHRIJVING (own top-level line, bound to `depreciation`)
                ============================================================ */}
            <AggregateRow label={t("Afschrijving")} rows={rows} sumFn={afschrijvingFor} testid="agg-afschrijving"
                          editable onCommit={(r, v) => saveField(r, "depreciation", v)}
                          getInitial={(r) => r.depreciation || 0} locked={locked} />

            {/* ============================================================
                EXPL. RESULTAAT (computed) + RENTE + NETTO EXPL. RESULTAAT + KAS + BANK
                ============================================================ */}
            <AggregateRow label={t("Exploitatieresultaat")} rows={rows} sumFn={resultaatFor} testid="agg-result" strong />
            {/* Rentelasten line item — user editable, bound to interest_paid field */}
            <tr className="hover:bg-muted/40">
              <td className="px-4 py-2 font-display uppercase text-xs tracking-widest sticky left-0 bg-card z-10 border-r-2 border-foreground/20">
                {t("Rentelasten")}
              </td>
              {rows.map((r) => (
                <td key={r.id} className="px-2 py-1 text-right">
                  <CellInput
                    key={`interest-exp-${r.id}-${r.interest_paid || 0}`}
                    initial={r.interest_paid || 0}
                    onCommit={(v) => saveField(r, "interest_paid", v)}
                    testid={`hist-cell-interest_paid-${r.year}`}
                    readOnly={locked}
                  />
                </td>
              ))}
            </tr>
            {/* Rentebaten line item — user editable, bound to interest_income field */}
            <tr className="hover:bg-muted/40">
              <td className="px-4 py-2 font-display uppercase text-xs tracking-widest sticky left-0 bg-card z-10 border-r-2 border-foreground/20">
                {t("Rentebaten")}
              </td>
              {rows.map((r) => (
                <td key={r.id} className="px-2 py-1 text-right">
                  <CellInput
                    key={`interest-inc-${r.id}-${r.interest_income || 0}`}
                    initial={r.interest_income || 0}
                    onCommit={(v) => saveField(r, "interest_income", v)}
                    testid={`hist-cell-interest_income-${r.year}`}
                    readOnly={locked}
                  />
                </td>
              ))}
            </tr>
            {/* Netto exploitatieresultaat = Exploitatieresultaat − (Rentelasten − Rentebaten) */}
            <AggregateRow label={t("Netto exploitatieresultaat")} rows={rows}
                          sumFn={(r) => resultaatFor(r) - ((Number(r.interest_paid) || 0) - (Number(r.interest_income) || 0))}
                          testid="agg-netto-result" strong />
            <tr>
              <td className="px-4 py-2 font-display uppercase text-xs tracking-widest sticky left-0 bg-card z-10 border-r-2 border-foreground/20">
                {t("Kas + bank 31/12")}
              </td>
              {rows.map((r) => (
                <td key={r.id} className="px-2 py-1 text-right">
                  <CellInput
                    key={`cbb-${r.id}-${r.cash_bank_balance || 0}`}
                    initial={r.cash_bank_balance || 0}
                    onCommit={(v) => saveField(r, "cash_bank_balance", v)}
                    testid={`hist-cell-cash_bank_balance-${r.year}`}
                    readOnly={locked}
                  />
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      )}
      {btwCtx && rows.length > 0 && (
        <BtwBlock t={t} testid="hist-btw-block" regime={btwCtx.regime} showActual readOnly={locked}
          onActualCommit={(key, paid) => { const r = rows.find((x) => String(x.year) === key); if (r) putRow(r, { btw_afdracht_teruggave: -paid }); }}
          columns={rows.map((r) => ({ key: String(r.year), label: String(r.year), block: btwForRow(r, btwCtx) }))} />
      )}

      {/* ---- Add-year dialog ---- */}
      <Dialog open={yearDlg} onOpenChange={setYearDlg}>
        <DialogContent className="max-w-sm" data-testid="hist-add-year-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight">{t("Nieuw boekjaar")}</DialogTitle>
            <DialogDescription>{t("Voeg een nieuwe kolom toe aan de matrix voor het opgegeven jaartal.")}</DialogDescription>
          </DialogHeader>
          <input
            type="number"
            value={newYear}
            onChange={(e) => setNewYear(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addYear(); } }}
            data-testid="hist-add-year-input"
            className="w-full border-2 border-foreground bg-background px-3 py-2 font-mono-num focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <DialogFooter>
            <button onClick={() => setYearDlg(false)} className="btn-brutal bg-background text-foreground px-4 py-2 font-display uppercase font-bold text-sm">
              {t("Annuleren")}
            </button>
            <button onClick={addYear} data-testid="hist-add-year-save"
              className="btn-brutal bg-primary text-primary-foreground px-4 py-2 font-display uppercase font-bold text-sm">
              {t("Toevoegen")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- AI PDF Extraction dialog ---- */}
      <AiPdfExtractDialog t={t} clubId={clubId}
                          open={aiPdfOpen}
                          onOpenChange={(v) => { setAiPdfOpen(v); if (!v) setAiPdfInitial(null); }}
                          locked={locked}
                          initialPreview={aiPdfInitial}
                          onImported={() => onChange?.()} />

      {/* ---- TRIAL Demo Sample picker ---- */}
      <DemoSampleDialog
        open={demoSampleOpen}
        onOpenChange={setDemoSampleOpen}
        clubId={clubId}
        onExtracted={(preview) => {
          setAiPdfInitial(preview);
          setAiPdfOpen(true);
        }}
      />

      {/* ---- Reset KOSTEN aggregate dialog ---- */}
      <Dialog open={resetDlg} onOpenChange={setResetDlg}>
        <DialogContent className="max-w-md" data-testid="hist-reset-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight">{t("Reset KOSTEN per jaar")}</DialogTitle>
            <DialogDescription>
              {t("Vul de correcte KOSTEN-aggregate (bedrijfslasten excl. afschrijving) per jaar in. Lege velden blijven ongewijzigd.")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {rows.map((r) => (
              <div key={r.id} className="flex items-center gap-2" data-testid={`hist-reset-row-${r.year}`}>
                <label className="font-display uppercase text-xs tracking-widest w-20">{r.year}</label>
                <input type="text" inputMode="numeric"
                  value={resetValues[r.year] ?? ""}
                  onChange={(e) => setResetValues((prev) => ({ ...prev, [r.year]: e.target.value.replace(/[^\d-]/g, "") }))}
                  data-testid={`hist-reset-input-${r.year}`}
                  className="flex-1 font-mono-num border-2 border-foreground bg-background px-3 py-1.5 text-right" />
              </div>
            ))}
          </div>
          <DialogFooter>
            <button onClick={() => setResetDlg(false)} className="btn-brutal bg-background text-foreground px-4 py-2 font-display uppercase font-bold text-sm">
              {t("Annuleren")}
            </button>
            <button onClick={submitReset} data-testid="hist-reset-save"
              className="btn-brutal bg-primary text-primary-foreground px-4 py-2 font-display uppercase font-bold text-sm">
              {t("Bijwerken")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// --- Sub-components ---

function AggregateRow({ label, rows, sumFn, testid, strong, editable, onCommit, getInitial, locked = false }) {
  return (
    <tr className={strong ? "bg-primary/10 font-bold" : "bg-muted/30 font-semibold"} data-testid={`agg-row-${testid}`}>
      <td className="px-4 py-2 font-display uppercase text-xs tracking-widest sticky left-0 bg-card z-10 border-r-2 border-foreground/20">
        {label}
      </td>
      {rows.map((r) => (
        <td key={r.id} className="px-3 py-1 text-right">
          {editable ? (
            <CellInput
              key={`${testid}-${r.id}-${getInitial(r)}`}
              initial={getInitial(r)}
              onCommit={(v) => onCommit(r, v)}
              testid={`cell-${testid}-${r.year}`}
              readOnly={locked}
              boldNum
            />
          ) : (
            <span className={`font-mono-num ${strong ? "text-primary" : "text-foreground/70"}`}
                  data-testid={`cell-${testid}-${r.year}`}>
              {fmtInt(sumFn(r))}
            </span>
          )}
        </td>
      ))}
    </tr>
  );
}

// Read-only system row: computes its value per year via `value(row)`.
function ComputedSystemRow({ label, rows, value, testid, t }) {
  return (
    <tr className="hover:bg-muted/40 italic" data-testid={`sys-row-${testid}`}>
      <td className="pl-4 pr-4 py-1 sticky left-0 bg-card z-10 border-r-2 border-foreground/20">
        <div className="flex items-center gap-2">
          <span className="w-3 shrink-0" />
          <span className="flex-1 truncate font-mono-num text-muted-foreground">{label}</span>
          <span className="text-[9px] font-display uppercase tracking-widest text-muted-foreground/50 shrink-0" title={t("Systeem-subpost — automatisch berekend als residu")}>
            {t("SYS")}
          </span>
        </div>
      </td>
      {rows.map((r) => {
        const v = value(r);
        return (
          <td key={r.id} className="px-3 py-1 text-right">
            <span className={`font-mono-num ${v < 0 ? "text-destructive" : "text-muted-foreground"}`}
                  data-testid={`cell-${testid}-${r.year}`}>
              {fmtInt(v)}
            </span>
          </td>
        );
      })}
    </tr>
  );
}

function SubpostRow({ sp, rows, cat, value, onCommit, onDragStart, onDragOver, onDrop, onDelete, testid, t, locked = false }) {
  return (
    <tr className="hover:bg-muted/40" draggable={!locked}
        onDragStart={locked ? undefined : onDragStart} onDragOver={locked ? undefined : onDragOver} onDrop={locked ? undefined : onDrop} data-testid={testid}>
      <td className="pl-4 pr-4 py-1 sticky left-0 bg-card z-10 border-r-2 border-foreground/20">
        <div className="flex items-center gap-2">
          {locked
            ? <span className="w-3 h-3 shrink-0" />
            : <GripVertical className="w-3 h-3 text-muted-foreground/60 cursor-grab shrink-0" />}
          <span className="flex-1 truncate font-mono-num text-muted-foreground" data-testid={`hist-sp-name-${cat}-${sp.name}`}>{sp.name}</span>
          {!locked && (
            <button onClick={onDelete} data-testid={`hist-sp-del-${cat}-${sp.name}`}
              className="opacity-30 hover:opacity-100 hover:text-destructive shrink-0" title={t("Subpost verwijderen")}>
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      </td>
      {rows.map((r) => (
        <td key={r.id} className="px-2 py-1 text-right">
          <CellInput
            key={`${cat}-${sp.name}-${r.id}-${value(r)}`}
            initial={value(r)}
            onCommit={(v) => onCommit(r, v)}
            testid={`hist-cell-${cat}-${sp.name}-${r.year}`}
            readOnly={locked}
            subtle
          />
        </td>
      ))}
    </tr>
  );
}

// System subpost: cannot be renamed, dragged, or deleted. Backed by a native
// historical field (other_income, depreciation).
function SystemSubpostRow({ label, rows, value, onCommit, testid, t }) {
  return (
    <tr className="hover:bg-muted/40" data-testid={`sys-row-${testid}`}>
      <td className="pl-4 pr-4 py-1 sticky left-0 bg-card z-10 border-r-2 border-foreground/20">
        <div className="flex items-center gap-2">
          <span className="w-3 shrink-0" />
          <span className="flex-1 truncate font-mono-num text-muted-foreground italic">{label}</span>
          <span className="text-[9px] font-display uppercase tracking-widest text-muted-foreground/50 shrink-0" title={t("Systeem-subpost — niet te verwijderen")}>
            {t("SYS")}
          </span>
        </div>
      </td>
      {rows.map((r) => (
        <td key={r.id} className="px-2 py-1 text-right">
          <CellInput
            key={`${testid}-${r.id}-${value(r)}`}
            initial={value(r)}
            onCommit={(v) => onCommit(r, v)}
            testid={`${testid}-${r.year}`}
            subtle
          />
        </td>
      ))}
    </tr>
  );
}

function AddSubpostRow({ cat, colSpan, adding, newName, onStart, onCancel, onChange, onSubmit, t, locked = false }) {
  if (locked) return null;
  return (
    <tr>
      <td colSpan={colSpan} className="pl-4 pr-4 py-1 sticky left-0 z-10 border-r-2 border-foreground/20 border-b border-dashed border-foreground/20">
        {adding ? (
          <div className="flex items-center gap-1 max-w-md">
            <input
              autoFocus
              value={newName}
              onChange={(e) => onChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); onSubmit(); }
                else if (e.key === "Escape") { e.preventDefault(); onCancel(); }
              }}
              placeholder={t("Naam nieuwe subpost")}
              data-testid={`hist-newsp-name-${cat}`}
              className="flex-1 border-2 border-foreground bg-background px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <button onClick={onSubmit} data-testid={`hist-newsp-save-${cat}`}
              className="text-primary hover:text-primary/80" title={t("Opslaan")}>
              <Check className="w-4 h-4" />
            </button>
            <button onClick={onCancel} className="text-muted-foreground" title={t("Annuleren")}>
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <button onClick={onStart} data-testid={`hist-newsp-btn-${cat}`}
            className="text-primary hover:text-primary/80 inline-flex items-center gap-1 font-display uppercase tracking-widest text-[11px] font-bold">
            <Plus className="w-3 h-3" /> {t("Subpost toevoegen")}
          </button>
        )}
      </td>
    </tr>
  );
}

function CellInput({ initial, onCommit, testid, subtle, boldNum, readOnly = false }) {
  // Whole-euro text cell with NL thousand separators + focus/blur formatting.
  return (
    <MoneyInput
      value={initial}
      onCommit={onCommit}
      testid={testid}
      readOnly={readOnly}
      className={`w-full bg-transparent px-2 py-1 font-mono-num text-right focus:outline-none focus:bg-primary/10 ${subtle ? "text-muted-foreground" : ""} ${boldNum ? "font-semibold" : ""} ${readOnly ? "cursor-not-allowed opacity-70" : ""}`}
    />
  );
}
